import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { supabase } from '../supabaseClient';
import LoadCard from './LoadCard';
import SortableLoadCard from './SortableLoadCard';
import LoadDetailsModal from './LoadDetailsModal';
import { notifyLoadAssigned, notifyStatusChange } from '../services/pushNotificationService';
import { logStatusChanged } from '../services/activityLogService';
import { faceSearchText } from '../utils/routeBuilderModel';
import { canTransition, labelOf, updateLoadStatusGuarded, completionBlockText } from '../services/statusTransitions';
// __BOARD_DRAFTS__ (2026-10-16) the hub's drafts, far left
import { draftFace, kindMatches, loadQuoteId, quoteSearch } from '../utils/draftFace';
import { listDrafts, deleteDraft, openDraftInHub } from '../services/hubDrafts';
import { useAuth } from '../AuthContext';

// Operational (non-terminal) pipeline only. Terminal statuses — complete,
// ready_to_bill, billed, archived — live in the Archive view (its own
// paginated query), not on the board. This keeps the board's fetch scoped to
// the working set so it can never silently re-hit the 1000-row cap, and removes
// the redundancy of the same terminal loads appearing in both surfaces.
// A load simply leaves the board once complete. __RTB_RETIRED__ ready_to_bill is
// retired (a complete job IS ready to bill; the hub bills it and marks it billed),
// so there is no board step after Complete. Every column below is still written by
// the driver app and the geofence engine (audit 2026-10-02) - keep them all.
const COLUMNS = [
  { id: 'pending', title: 'Pending', color: '#8b949e' },
  { id: 'assigned', title: 'Assigned', color: '#58a6ff' },
  { id: 'en_route', title: 'En Route', color: '#a371f7' },
  { id: 'at_pickup', title: 'At Pickup', color: '#d29922' },
  { id: 'loading', title: 'Loading', color: '#e3b341' },
  { id: 'in_transit', title: 'In Transit', color: '#3fb950' },
  { id: 'at_delivery', title: 'At Delivery', color: '#238636' },
  { id: 'unloading', title: 'Unloading', color: '#2ea043' },
  { id: 'pending_completion', title: '⏳ Confirm Complete', color: '#f85149' },
];

// The board fetches exactly these statuses (derived from the columns above).
const OPERATIONAL_STATUSES = COLUMNS.map((c) => c.id);


function DroppableColumn({ column, children, loadCount }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  return (
    <div
      className="kanban-column"
      style={{
        minWidth: '280px',
        backgroundColor: isOver ? 'var(--surface-2)' : 'var(--surface-1)',
        borderRadius: 'var(--radius-lg)',
        padding: '1rem',
        border: isOver ? `1px solid ${column.color}` : '1px solid var(--border-default)',
        transition: 'background-color 0.15s, border-color 0.15s'
      }}
    >
      <div style={{ marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: `3px solid ${column.color}` }}>
        <h3 style={{ margin: 0, color: column.color, fontSize: '1rem', fontWeight: 'bold' }}>
          {column.title}
        </h3>
        <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          {loadCount} loads
        </p>
      </div>
      <div ref={setNodeRef} style={{ minHeight: '400px', borderRadius: '5px', padding: '0.5rem' }}>
        {children}
      </div>
    </div>
  );
}

// __BOARD_DRAFTS__ (2026-10-16, Henry) "Drafts" -- the hub's drafts (the same as the website's My Drafts / All Users'
// Drafts) as the FAR-LEFT column. NOT a drop target and nothing in it is draggable (no useDroppable / useSortable): a
// draft is not a job and is never dragged into a status column -- it becomes one only by opening it in the hub's
// booking form and pressing Create Job. Click = open it there (new tab); Delete asks first.
// __SAVED_QUOTES__ (2026-10-16) saved quotes are drafts too: a QUOTE Q-7K3F chip with the price, and All | Drafts | Quotes
// at the top of the column (remembered per user on this browser).
const KIND_LABEL = { all: 'All', draft: 'Drafts', quote: 'Quotes' };
function DraftsColumn({ drafts, scope, error, onOpen, onDelete, canDelete, kind, onKind }) {
  const now = Date.now();
  const quotes = drafts.filter((d) => draftFace(d, now).quoteId).length;
  return (
    <div className="kanban-column" data-drafts-column="1" style={{
      minWidth: '280px', backgroundColor: 'var(--surface-1)', borderRadius: 'var(--radius-lg)', padding: '1rem',
      border: '1px dashed var(--border-default)',
    }}>
      <div style={{ marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '3px solid var(--text-secondary)' }}>
        <h3 style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '1rem', fontWeight: 'bold' }}>Drafts</h3>
        <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          {drafts.length} {drafts.length === 1 ? 'item' : 'items'}{quotes ? ' (' + quotes + ' quote' + (quotes === 1 ? '' : 's') + ')' : ''} · {scope === 'all' ? "everyone's" : 'yours'} · not jobs yet
        </p>
        <div role="group" aria-label="Show drafts, quotes or both" data-drafts-kind="1" style={{ display: 'flex', gap: '0.3rem', marginTop: '0.45rem' }}>
          {['all', 'draft', 'quote'].map((k) => (
            <button key={k} type="button" aria-pressed={kind === k} onClick={() => onKind(k)}
              style={{ fontSize: '0.7rem', padding: '0.05rem 0.55rem', borderRadius: '999px', cursor: 'pointer', lineHeight: 1.5,
                border: '1px solid ' + (kind === k ? 'var(--color-primary, #1f6feb)' : 'var(--border-default)'),
                background: kind === k ? 'var(--color-primary, #1f6feb)' : 'transparent',
                color: kind === k ? '#fff' : 'var(--text-secondary)' }}>{KIND_LABEL[k]}</button>
          ))}
        </div>
      </div>
      <div style={{ minHeight: '400px', padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {error && <div style={{ color: 'var(--color-danger)', fontSize: '0.8rem' }}>Could not load the drafts: {error}</div>}
        {drafts.map((d) => {
          const f = draftFace(d, now);
          return (
            <div key={d.id} role="button" tabIndex={0} title={f.tooltip + '\n\nClick to open it in the booking form on the hub.'}
              data-draft-card="1"
              onClick={() => onOpen(d)}
              onKeyDown={(e) => { if (e.key === 'Enter') onOpen(d); }}
              style={{
                backgroundColor: 'var(--surface-1)', border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-md)',
                padding: '1rem', cursor: 'pointer',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.3rem' }}>
                <div data-card-face="customer" style={{ fontSize: '1rem', color: 'var(--text-primary)', fontWeight: 'bold', minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.customer}</div>
                {f.quoteId ? (
                  <span data-quote-chip="1" title={'Saved quote ' + f.quoteId} style={{ fontSize: '0.65rem', fontWeight: 'bold', letterSpacing: '0.06em',
                    color: 'var(--color-primary, #1f6feb)', border: '1px solid var(--color-primary, #1f6feb)', borderRadius: '4px',
                    padding: '0 0.35rem', flexShrink: 0, whiteSpace: 'nowrap' }}>QUOTE {f.quoteId}</span>
                ) : (
                  <span style={{ fontSize: '0.65rem', fontWeight: 'bold', letterSpacing: '0.08em', color: 'var(--text-secondary)',
                    border: '1px solid var(--border-default)', borderRadius: '4px', padding: '0 0.35rem', flexShrink: 0 }}>DRAFT</span>
                )}
              </div>
              {f.quoteId && f.price && <div data-quote-price="1" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>{f.price} quoted</div>}
              <div data-card-face="route" style={{ fontSize: '0.88rem', color: 'var(--text-primary)', lineHeight: 1.3, marginBottom: '0.35rem' }}>
                {f.lines.map((l, i) => <div key={i} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l}</div>)}
              </div>
              {f.when && <div style={{ fontSize: '0.75rem', color: 'var(--color-warning)', fontWeight: '500', marginBottom: '0.35rem' }}>{f.when}</div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem',
                color: 'var(--text-secondary)', paddingTop: '0.5rem', borderTop: '1px solid var(--border-default)' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.saved}</span>
                {canDelete(d) && (
                  <button type="button" aria-label={'Delete the draft for ' + f.customer}
                    onClick={(e) => { e.stopPropagation(); onDelete(d); }}
                    style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', fontSize: '0.75rem', padding: 0, flexShrink: 0 }}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {!drafts.length && !error && <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', textAlign: 'center' }}>{kind === 'quote' ? 'No saved quotes' : 'No drafts'}</div>}
      </div>
    </div>
  );
}

// __PORTAL_JUMP__ (2026-10-27) the hub's "Hauling board" button (Customers -> Customer portal: a customer's message,
// booking request or priority card) opens hub /goto/fleet-manager?job=BK-..., which lands here as ?job=. The board then
// searches that job and opens its Load Details - also a finished job that is no longer on the columns. Only a job
// number's shape is read; the param leaves the address bar at once.
export function jobParam(search) {
  try {
    const v = new URLSearchParams(search || '').get('job') || '';
    return /^BK-[0-9A-Za-z-]{4,30}$/.test(v) ? v : '';
  } catch (e) {
    return '';
  }
}

function KanbanBoard() {
  const { user } = useAuth() || {};
  // __BOARD_DRAFTS__ null = no column (the hub does not give this login drafts, or it cannot be reached at all)
  const [drafts, setDrafts] = useState(null);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // __PORTAL_JUMP__ ?job=BK-...: search it, open it, then forget it
  const [jumpJob, setJumpJob] = useState(() => jobParam(window.location.search));
  useEffect(() => {
    if (!jumpJob) return;
    setSearchTerm(jumpJob);
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('job');
      window.history.replaceState({}, document.title, u.pathname + u.search + u.hash);
    } catch (e) { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // __SAVED_QUOTES__ All | Drafts | Quotes for the Drafts column, remembered per user (this browser)
  const kindKey = 'hb_drafts_kind:' + ((user && user.id) || 'anon');
  const [draftKind, setDraftKind] = useState('all');
  useEffect(() => {
    try { const k = window.localStorage.getItem(kindKey); setDraftKind(k === 'draft' || k === 'quote' ? k : 'all'); } catch (e) { /* private window */ }
  }, [kindKey]);
  const chooseDraftKind = (k) => {
    setDraftKind(k);
    try { window.localStorage.setItem(kindKey, k); } catch (e) { /* private window */ }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Fetch loads from database
  useEffect(() => {
    autoArchiveBilledLoads();
    fetchLoads();
    
    // Set up real-time subscription
    const subscription = supabase
      .channel('loads')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'loads' 
      }, (payload) => {
        console.log('Real-time update:', payload);
        fetchLoads();
      })
      .subscribe();

    // __STATUS_TRUTH__ (2026-10-28) loads is not in the supabase_realtime publication, so the subscription above
    // never fires: a job completed in Trello / the Driver app stayed in its old column until a reload. Re-read every
    // 30 s while the tab is visible, and at once when it comes back into view.
    const poll = setInterval(() => { if (!document.hidden) fetchLoads({ quiet: true }); }, 30000);
    const onShow = () => { if (!document.hidden) fetchLoads({ quiet: true }); };
    document.addEventListener('visibilitychange', onShow);

    return () => {
      supabase.removeChannel(subscription);
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onShow);
    };
  }, []);

  // __BOARD_DRAFTS__ fetched on open, when the tab comes back into view, and every minute
  const fetchDrafts = async () => {
    const r = await listDrafts();
    if (r.ok) setDrafts({ items: r.drafts, scope: r.scope, error: '' });
    else if (r.status === 401 || r.status === 403) setDrafts(null);
    else setDrafts((d) => ({ items: d ? d.items : [], scope: d ? d.scope : 'mine', error: r.message }));
  };
  useEffect(() => {
    fetchDrafts();
    const onVis = () => { if (document.visibilityState === 'visible') fetchDrafts(); };
    document.addEventListener('visibilitychange', onVis);
    const timer = setInterval(fetchDrafts, 60000);
    return () => { document.removeEventListener('visibilitychange', onVis); clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const canDeleteDraft = (d) => !!drafts && (drafts.scope === 'all' || (!!user && d._user_id === user.id));
  const onDeleteDraft = async (d) => {
    const f = draftFace(d);
    if (!window.confirm('Delete this draft?\n\n' + f.customer + '\n' + f.lines.join('\n') + '\n\nIt is deleted on the hub too. This cannot be undone.')) return;
    const r = await deleteDraft(d.id);
    if (!r.ok) window.alert('Not deleted: ' + r.message);
    fetchDrafts();
  };
  // Auto-archive: move billed loads older than 7 days to archived
  const autoArchiveBilledLoads = async () => {
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const { data, error } = await supabase
        .from('loads')
        .update({ 
          status: 'archived', 
          archived_at: new Date().toISOString() 
        })
        .eq('status', 'billed')
        .lt('billed_at', sevenDaysAgo.toISOString());
      
      if (error) console.warn('Auto-archive error:', error);
    } catch (err) {
      console.warn('Auto-archive failed:', err);
    }
  };
  const fetchLoads = async (opts = {}) => {
    if (!opts.quiet) setLoading(true);   // __STATUS_TRUTH__ the 30-second re-read does not blank the board
    // Range-paginated loop: pull the operational set in 1000-row pages until a
    // short page signals the end. This can NEVER silently truncate — if the
    // working set ever grows past a single page, we simply fetch more pages.
    // (Today ~958 pending fits in one page; the loop is growth insurance.)
    const PAGE = 1000;
    const all = [];
    let from = 0;
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data, error } = await supabase
          .from('loads')
          .select(`
            *,
            customer:customers(company_name, phone),
            driver:drivers(user_id(name), phone),
            truck:trucks(truck_number, license_plate),
            load_stops(id, stop_type, stop_sequence, address, place_name, time_window_start)
          `)
          .in('status', OPERATIONAL_STATUSES)
          .order('created_at', { ascending: false })
          .range(from, from + PAGE - 1);

        if (error) {
          console.error('Error fetching loads:', error);
          break;
        }
        const batch = data || [];
        all.push(...batch);
        if (batch.length < PAGE) break; // short page → no more rows
        from += PAGE;
      }
      setLoads(all);
    } catch (err) {
      console.error('Error fetching loads:', err);
    } finally {
      setLoading(false);
    }
  };

  // __PORTAL_JUMP__ open the job once the board has its loads (a finished job is read on its own)
  useEffect(() => {
    if (!jumpJob || loading) return;
    const hit = loads.find((l) => l.load_number === jumpJob);
    if (hit) { setSelectedLoad(hit); setJumpJob(''); return; }
    let live = true;
    (async () => {
      try {
        const { data } = await supabase
          .from('loads')
          .select(`
            *,
            customer:customers(company_name, phone),
            driver:drivers(user_id(name), phone),
            truck:trucks(truck_number, license_plate),
            load_stops(id, stop_type, stop_sequence, address, place_name, time_window_start)
          `)
          .eq('load_number', jumpJob)
          .limit(1);
        if (live && data && data[0]) setSelectedLoad(data[0]);
      } catch (e) {
        console.warn('[jump] job read failed:', e && e.message);
      }
      if (live) setJumpJob('');
    })();
    return () => { live = false; };
  }, [jumpJob, loading, loads]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeLoadId = active.id;
    const overColumnId = over.id;

    // Check if we're dragging over a column
    const isOverColumn = COLUMNS.some(col => col.id === overColumnId);
    
    if (isOverColumn) {
      // We're over a column, this will be handled in handleDragEnd
      return;
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeLoadId = active.id;
    let newStatus = over.id;

    // Check if we dropped over another load card (get its parent column)
    const isOverColumn = COLUMNS.some(col => col.id === newStatus);
    if (!isOverColumn) {
      // We dropped over a load card, find which column it belongs to
      const overLoad = loads.find(l => l.id === over.id);
      if (overLoad) {
        newStatus = overLoad.status;
      }
    }

    const draggedLoadBefore = loads.find(l => l.id === activeLoadId);
    const fromStatus = draggedLoadBefore ? draggedLoadBefore.status : null;
    if (!fromStatus || fromStatus === newStatus) return;

    // Transition guard: a drag is a status write like any other. Off-map jumps
    // (e.g. Pending -> Unloading) need an explicit override from dispatch.
    if (!canTransition(fromStatus, newStatus)) {
      const ok = window.confirm(
        `"${labelOf(fromStatus)}" -> "${labelOf(newStatus)}" is not a normal step.\n\n` +
        'The driver app and the geofence will treat the load as if it really is there. Force it anyway?'
      );
      if (!ok) return;
    }

    // Conditional write: only if the load is still where this board last saw it.
    const res = await updateLoadStatusGuarded(activeLoadId, fromStatus, newStatus);
    if (!res.ok) {
      if (res.reason === 'conflict') {
        window.alert(`Not moved: this load is already "${labelOf(res.current)}" (changed by the driver app or the geofence). The board will refresh.`);
        fetchLoads();
      } else {
        console.error('Failed to update load status:', res.error);
        // __CARGO_REQUIRED__ say why instead of failing silently
        window.alert(completionBlockText(res.error) || ('Could not move the load: ' + ((res.error && res.error.message) || 'unknown error')));
        fetchLoads();
      }
    } else {
      // Log activity
      const draggedLoad = loads.find(l => l.id === activeLoadId);
      if (draggedLoad) logStatusChanged(draggedLoad, draggedLoad.status, newStatus);
      // Send push notification to driver
      const updatedLoad = loads.find(l => l.id === activeLoadId);
      if (updatedLoad?.driver_id) {
        if (newStatus === 'assigned') {
          notifyLoadAssigned(updatedLoad.driver_id, updatedLoad).catch(e => console.warn('Push failed:', e));
        } else {
          notifyStatusChange(updatedLoad.driver_id, { ...updatedLoad, load_number: updatedLoad.load_number }, newStatus).catch(e => console.warn('Push failed:', e));
        }
      }
    }
  };

  // Kanban search is scoped to the loaded operational set by design — searching
  // the board means searching in-flight work. Terminal/archived loads are found
  // via the Archive view's server-side search, not here.
  const getLoadsByStatus = (status) => {
    return loads.filter(load => {
      // First filter by status
      if (load.status !== status) return false;
      
      // Then filter by search term if present
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const loadNumber = load.load_number?.toLowerCase() || '';
        const customerName = (load.customer?.company_name || load.customer_name || '').toLowerCase();
        const commodity = load.commodity?.toLowerCase() || '';
        const driverName = load.driver?.user_id?.name?.toLowerCase() || '';
        const truckNumber = load.truck?.truck_number?.toLowerCase() || '';
        // __SAVED_QUOTES__ a job booked from a saved quote is found by the quote's number too
        const quoteKey = quoteSearch(loadQuoteId(load));
        const qTerm = term.replace(/\s+/g, '');
        
        // __CARD_FACE__ the face shows From -> To: its names and addresses are searchable too
        return loadNumber.includes(term) ||
               customerName.includes(term) ||
               faceSearchText(load).includes(term) ||
               commodity.includes(term) ||
               driverName.includes(term) ||
               truckNumber.includes(term) ||
               (!!quoteKey && !!qTerm && quoteKey.includes(qTerm));
      }
      
      return true;
    });
  };

  // Get columns to display based on status filter
  const getVisibleColumns = () => {
    if (statusFilter === 'all') return COLUMNS;
    if (statusFilter === 'active') {
      return COLUMNS.filter(col => col.id !== 'pending');
    }
    if (statusFilter === 'pending') {
      return COLUMNS.filter(col => col.id === 'pending');
    }
    return COLUMNS;
  };

  const visibleColumns = getVisibleColumns();

  const activeLoad = loads.find(load => load.id === activeId);

  if (loading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem', 
        color: 'var(--accent-blue)',
        fontSize: '1.5rem'
      }}>
        Loading loads...
      </div>
    );
  }

  return (
<div>
      {/* Header with Search */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        <h2 style={{ color: 'var(--text-primary)', margin: 0 }}>
          Load Management
        </h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '0.6rem 1rem',
              backgroundColor: 'var(--surface-input)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Only</option>
            <option value="pending">Pending Only</option>
          </select>
          <input
            type="text"
            placeholder="Search loads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '0.6rem 1rem',
              backgroundColor: 'var(--surface-input)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              fontSize: '0.9rem',
              minWidth: '200px',
              maxWidth: '300px'
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                padding: '0.6rem 1rem',
                backgroundColor: 'var(--btn-secondary-bg)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Kanban Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="kanban-container" style={{
          display: 'flex',
          gap: '1rem',
          overflowX: 'auto',
          paddingBottom: '1rem'
        }}>
          {drafts && statusFilter !== 'active' && (
            <DraftsColumn
              drafts={drafts.items.filter((d) => kindMatches(d, draftKind)
                && (!searchTerm.trim() || draftFace(d).search.includes(searchTerm.trim().toLowerCase())
                  || draftFace(d).search.includes(searchTerm.replace(/\s+/g, '').toLowerCase())))}
              kind={draftKind}
              onKind={chooseDraftKind}
              scope={drafts.scope}
              error={drafts.error}
              onOpen={(d) => openDraftInHub(d.id)}
              onDelete={onDeleteDraft}
              canDelete={canDeleteDraft}
            />
          )}
          {visibleColumns.map((column) => {
            const columnLoads = getLoadsByStatus(column.id);
            return (
              <SortableContext
                key={column.id}
                id={column.id}
                items={columnLoads.map(l => l.id)}
                strategy={verticalListSortingStrategy}
              >
                <DroppableColumn column={column} loadCount={columnLoads.length}>
                  {columnLoads.map((load) => (
                    <SortableLoadCard
                      key={load.id}
                      load={load}
                      onClick={(load) => setSelectedLoad(load)}
                    />
                  ))}
                </DroppableColumn>
              </SortableContext>
            );
          })}
        </div>

        <DragOverlay>
          {activeId && activeLoad ? (
            <div style={{ cursor: 'grabbing' }}>
              <LoadCard load={activeLoad} isDragging={true} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Load Details Modal */}
      {selectedLoad && (
        <LoadDetailsModal
          load={selectedLoad}
          onClose={() => setSelectedLoad(null)}
          onUpdate={() => {
            setSelectedLoad(null);
            fetchLoads();
          }}
        />
      )}
    </div>
  );
}

export default KanbanBoard;
